import React, { createContext, useContext, useEffect } from "react";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExtensionIcon from "@mui/icons-material/Extension";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import SearchIcon from "@mui/icons-material/Search";
import StarsIcon from "@mui/icons-material/Stars";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { IPHONE_BG_PLAY_FALLBACK, screenshotIphoneBgSrc } from "../lib/screenshotIphoneBackgrounds";
import PlayBackground from "./PlayBackground";
import ScreenshotPlayPanel from "./ScreenshotPlayPanel";
import { JourneyMapColumn, MasterJourneyColumn } from "./ScreenshotMarketingLayouts";

export const LANDSCAPE_AD_W = 1200;
export const LANDSCAPE_AD_H = 628;
export const SQUARE_AD_W = 1200;
export const SQUARE_AD_H = 1200;

/** @typedef {'landscape' | 'square'} GoogleAdVariant */

const AdVariantContext = createContext(/** @type {GoogleAdVariant} */ ("landscape"));

function useAdVariant() {
    return useContext(AdVariantContext);
}

function useIsSquare() {
    return useAdVariant() === "square";
}

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';

const PHONE_W = 220;
const PHONE_H = 460;
const PHONE_W_SQ = 280;
const PHONE_H_SQ = 580;

function lockOverflow() {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
        document.documentElement.style.overflow = prevHtml;
        document.body.style.overflow = prevBody;
    };
}

/** Full-bleed Google Ads canvas — landscape 1200×628 or square 1200×1200. */
function AdCanvas({ children, sx = {} }) {
    const variant = useAdVariant();
    const isSquare = variant === "square";
    const width = isSquare ? SQUARE_AD_W : LANDSCAPE_AD_W;
    const height = isSquare ? SQUARE_AD_H : LANDSCAPE_AD_H;
    useEffect(() => lockOverflow(), []);
    return (
        <Box
            data-screenshot-frame={
                isSquare ? "word-wheel-square-ad" : "word-wheel-landscape-ad"
            }
            data-google-ad-landscape={isSquare ? undefined : "1"}
            data-google-ad-square={isSquare ? "1" : undefined}
            sx={{
                position: "fixed",
                inset: 0,
                width,
                height,
                overflow: "hidden",
                boxSizing: "border-box",
                zIndex: 1,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function CoverBg({ src, fallback = "#0c4a6e", overlay }) {
    return (
        <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: fallback,
                    backgroundImage: src ? `url("${src}")` : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            {overlay ? (
                <Box sx={{ position: "absolute", inset: 0, background: overlay }} />
            ) : null}
        </Box>
    );
}

function AdPhone({ rotate = 0, width, height, children, sx = {} }) {
    const isSquare = useIsSquare();
    const w = width ?? (isSquare ? PHONE_W_SQ : PHONE_W);
    const h = height ?? (isSquare ? PHONE_H_SQ : PHONE_H);
    return (
        <Box
            sx={{
                width: w,
                height: h,
                transform: rotate ? `rotate(${rotate}deg)` : "none",
                transformOrigin: "center center",
                borderRadius: "28px",
                p: "5px",
                background:
                    "linear-gradient(160deg, rgba(226,232,240,0.7) 0%, rgba(15,23,42,0.95) 48%, rgba(56,189,248,0.35) 100%)",
                boxShadow:
                    "0 18px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(186,230,253,0.25)",
                boxSizing: "border-box",
                flexShrink: 0,
                ...sx,
            }}
        >
            <Box
                sx={{
                    borderRadius: "23px",
                    overflow: "hidden",
                    bgcolor: "#0f172a",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function PlayPhone({
    puzzle,
    foundWords,
    scene,
    background,
    theme,
    rotate = 0,
    width,
    height,
    sx,
    clueMode = false,
}) {
    return (
        <AdPhone rotate={rotate} width={width} height={height} sx={sx}>
            {puzzle ? (
                <Box sx={{ flex: 1, minHeight: 0, height: "100%", display: "flex" }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            "& > *": {
                                flex: 1,
                                minHeight: 0,
                                height: "100% !important",
                                borderRadius: "0 !important",
                            },
                        }}
                    >
                        <ScreenshotPlayPanel
                            puzzle={puzzle}
                            foundWords={foundWords}
                            journeyLevel={scene?.journeyLevel}
                            background={background || scene?.background || "iphone_1"}
                            theme={theme || scene?.theme || "random"}
                            fill
                            showLevelTitle
                            clueMode={clueMode}
                        />
                    </Box>
                </Box>
            ) : (
                <PlayBackground
                    background={background || scene?.background || "iphone_1"}
                    theme={theme || scene?.theme || "random"}
                />
            )}
        </AdPhone>
    );
}

function WoodTile({ letter, sx = {} }) {
    return (
        <Box
            sx={{
                width: 52,
                height: 52,
                borderRadius: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: TITLE_STACK,
                fontWeight: 900,
                fontSize: 26,
                color: "#431407",
                background: "linear-gradient(160deg, #fde68a 0%, #d97706 55%, #92400e 100%)",
                border: "2px solid rgba(120,53,15,0.55)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.35)",
                ...sx,
            }}
        >
            {letter}
        </Box>
    );
}

function PalmSilhouette({ side = "left" }) {
    const flip = side === "right";
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                [side]: -20,
                bottom: -30,
                width: 180,
                height: 280,
                zIndex: 1,
                opacity: 0.55,
                transform: flip ? "scaleX(-1)" : "none",
                pointerEvents: "none",
                background:
                    "radial-gradient(ellipse 40% 30% at 30% 20%, #14532d 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 55% 35%, #166534 0%, transparent 65%), linear-gradient(180deg, transparent 0%, #14532d 55%, #052e16 100%)",
                filter: "blur(0.2px)",
            }}
        />
    );
}

function AdHeadline({ title, subtitle, dark = false, sx = {} }) {
    const isSquare = useIsSquare();
    return (
        <Box sx={{ position: "relative", zIndex: 3, textAlign: "center", px: 3, ...sx }}>
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: isSquare ? 52 : 42,
                    lineHeight: 1.05,
                    letterSpacing: "0.03em",
                    color: dark ? "#0c4a6e" : "#f8fafc",
                    textShadow: dark ? "none" : "0 3px 18px rgba(2,6,23,0.65)",
                    whiteSpace: "pre-line",
                }}
            >
                {title}
            </Typography>
            {subtitle ? (
                <Typography
                    sx={{
                        m: 0,
                        mt: 1,
                        fontWeight: 600,
                        fontSize: isSquare ? 22 : 18,
                        color: dark ? "#334155" : "rgba(224,242,254,0.92)",
                        textShadow: dark ? "none" : "0 2px 10px rgba(2,6,23,0.5)",
                    }}
                >
                    {subtitle}
                </Typography>
            ) : null}
        </Box>
    );
}

function FloatBadge({ children, sx = {} }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 3,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                bgcolor: "rgba(15,23,42,0.82)",
                border: "1px solid rgba(125,211,252,0.45)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                backdropFilter: "blur(8px)",
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

/** 01 — Dual-device fan + wooden tiles. */
function LandscapeHero({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_1")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(115deg, rgba(2,6,23,0.55) 0%, rgba(8,47,73,0.35) 45%, rgba(2,6,23,0.5) 100%)"
            />
            <PalmSilhouette side="left" />
            <PalmSilhouette side="right" />
            {["W", "O", "R", "D"].map((ch, i) => (
                <WoodTile
                    key={`w-${ch}`}
                    letter={ch}
                    sx={{
                        position: "absolute",
                        zIndex: 2,
                        left: 40 + i * 58,
                        top: (isSquare ? 200 : 120) + (i % 2) * 18,
                        transform: `rotate(${-12 + i * 7}deg)`,
                    }}
                />
            ))}
            {["Q", "U", "E", "S", "T"].map((ch, i) => (
                <WoodTile
                    key={`q-${ch}`}
                    letter={ch}
                    sx={{
                        position: "absolute",
                        zIndex: 2,
                        right: 36 + i * 54,
                        bottom: (isSquare ? 140 : 70) + (i % 2) * 16,
                        transform: `rotate(${10 - i * 5}deg)`,
                        width: 46,
                        height: 46,
                        fontSize: 22,
                    }}
                />
            ))}
            <AdHeadline
                title={"UNLEASH YOUR\nWORD POWER"}
                subtitle="Thousands of handcrafted word puzzles await."
                sx={{ pt: isSquare ? 5 : 2.5 }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: isSquare ? 64 : 28,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 3,
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    rotate={-10}
                    width={isSquare ? 340 : 280}
                    height={isSquare ? 680 : 520}
                />
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    rotate={8}
                    width={isSquare ? 340 : 280}
                    height={isSquare ? 680 : 520}
                    sx={{ mb: -1 }}
                />
            </Box>
        </AdCanvas>
    );
}

/** 02 — Zoomed UI + floating success panel. */
function LandscapeGameplay({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_2")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(90deg, rgba(2,6,23,0.25) 0%, rgba(8,47,73,0.15) 50%, rgba(2,6,23,0.4) 100%)"
            />
            {[0, 1, 2, 3, 4, 5].map((i) => (
                <Box
                    key={i}
                    aria-hidden
                    sx={{
                        position: "absolute",
                        zIndex: 1,
                        width: 10 + (i % 3) * 6,
                        height: 6 + (i % 2) * 4,
                        borderRadius: "40%",
                        bgcolor: "rgba(125,211,252,0.55)",
                        left: `${12 + i * 14}%`,
                        top: `${30 + (i % 4) * 12}%`,
                        boxShadow: "0 0 12px rgba(56,189,248,0.5)",
                        transform: `rotate(${i * 20}deg)`,
                    }}
                />
            ))}
            <AdHeadline title="SWIPE TO CONNECT" sx={{ pt: isSquare ? 5 : 2 }} />
            <Box
                sx={{
                    position: "absolute",
                    left: isSquare ? 80 : 48,
                    bottom: isSquare ? 48 : 20,
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    width={isSquare ? 360 : 280}
                    height={isSquare ? 720 : 520}
                    rotate={-3}
                />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: isSquare ? 72 : 56,
                    top: isSquare ? 280 : 150,
                    width: isSquare ? 340 : 300,
                }}
            >
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#86efac", mb: 1 }}>
                    Word found!
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 0.6,
                    }}
                >
                    {["W", "O", "R", "D", "S"].map((ch) => (
                        <Box
                            key={ch}
                            sx={{
                                aspectRatio: "1",
                                borderRadius: 1,
                                bgcolor: "rgba(34,197,94,0.25)",
                                border: "1px solid rgba(74,222,128,0.7)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 900,
                                color: "#bbf7d0",
                                fontSize: 18,
                                boxShadow: "0 0 14px rgba(34,197,94,0.45)",
                            }}
                        >
                            {ch}
                        </Box>
                    ))}
                </Box>
                <Typography sx={{ mt: 1.25, fontSize: 12, color: "rgba(226,232,240,0.85)" }}>
                    Glowing success on the grid — keep the streak going.
                </Typography>
            </FloatBadge>
        </AdCanvas>
    );
}

/** 02 — Clue: show the meaning, keep the word hidden. */
function LandscapeClue({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_2")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(90deg, rgba(2,6,23,0.28) 0%, rgba(8,47,73,0.16) 50%, rgba(2,6,23,0.42) 100%)"
            />
            <AdHeadline title="READ THE CLUE" sx={{ pt: isSquare ? 5 : 2 }} />
            <Box
                sx={{
                    position: "absolute",
                    left: isSquare ? 80 : 48,
                    bottom: isSquare ? 48 : 20,
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    width={isSquare ? 360 : 280}
                    height={isSquare ? 720 : 520}
                    rotate={-3}
                    clueMode
                />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: isSquare ? 72 : 56,
                    top: isSquare ? 280 : 150,
                    width: isSquare ? 340 : 300,
                    bgcolor: "rgba(255, 251, 235, 0.96)",
                    border: "2px solid #f59e0b",
                    boxShadow: "0 0 0 3px rgba(251, 191, 36, 0.35), 0 12px 28px rgba(180, 83, 9, 0.35)",
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: "#f59e0b",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(180, 83, 9, 0.45)",
                        }}
                    >
                        <SearchIcon sx={{ color: "#fffbeb", fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: "0.16em", color: "#b45309", mb: 0.5 }}>
                            CLUE
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#1c1917", lineHeight: 1.35, mb: 0.75 }}>
                            See the meaning. Find the word.
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#57534e", lineHeight: 1.4 }}>
                            Each numbered cell has a definition. Read it, then swipe the wheel to spell the answer.
                        </Typography>
                    </Box>
                </Stack>
            </FloatBadge>
        </AdCanvas>
    );
}

/** 03 — Split showcase + level badge. */
function LandscapeProgression({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_3")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(120deg, rgba(2,6,23,0.45) 0%, rgba(15,23,42,0.25) 50%, rgba(2,6,23,0.5) 100%)"
            />
            <AdHeadline
                title="1000+ UNIQUE PUZZLES"
                sx={{ pt: isSquare ? 5 : 2, textAlign: "left", pl: 4 }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: isSquare ? 72 : 56,
                    bottom: isSquare ? 56 : 24,
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    background={scene?.background || "iphone_3"}
                    rotate={-4}
                    width={isSquare ? 320 : 250}
                    height={isSquare ? 640 : 500}
                />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: isSquare ? 72 : 64,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: isSquare ? 360 : 320,
                    textAlign: "center",
                    py: 2.5,
                    background:
                        "linear-gradient(160deg, rgba(30,27,75,0.92) 0%, rgba(15,23,42,0.9) 100%)",
                }}
            >
                <StarsIcon sx={{ fontSize: 36, color: "#fde68a", mb: 0.5 }} />
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 28,
                        color: "#fef3c7",
                        lineHeight: 1.15,
                    }}
                >
                    LEVEL 100+
                    <br />
                    REACHED!
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.5 }}>
                    {["+250", "★ ★ ★", "BONUS"].map((t) => (
                        <Box
                            key={t}
                            sx={{
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 999,
                                bgcolor: "rgba(250,204,21,0.15)",
                                border: "1px solid rgba(250,204,21,0.45)",
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#fde68a",
                            }}
                        >
                            {t}
                        </Box>
                    ))}
                </Stack>
            </FloatBadge>
        </AdCanvas>
    );
}

/** 02 — Journey map chapter cards. */
function LandscapeJourney({ scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_2")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(15,23,42,0.18) 100%)"
            />
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: isSquare ? 36 : 16,
                    bottom: isSquare ? 36 : 16,
                    transform: "translateX(-50%)",
                    width: isSquare ? 460 : 360,
                    zIndex: 3,
                    overflow: "hidden",
                    display: "flex",
                }}
            >
                <JourneyMapColumn />
            </Box>
        </AdCanvas>
    );
}

/** 08 — Master journey unlock. */
function LandscapeMasterJourney({ scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_2")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(15,23,42,0.18) 100%)"
            />
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: isSquare ? 36 : 16,
                    bottom: isSquare ? 36 : 16,
                    transform: "translateX(-50%)",
                    width: isSquare ? 460 : 360,
                    zIndex: 3,
                    overflow: "hidden",
                    display: "flex",
                }}
            >
                <MasterJourneyColumn />
            </Box>
        </AdCanvas>
    );
}

/** 05 — Dictionary focus card. */
function LandscapeDictionary({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_4")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(135deg, rgba(236,254,255,0.18) 0%, rgba(2,6,23,0.38) 55%, rgba(2,6,23,0.5) 100%)"
            />
            {["A", "B", "C", "M", "Z"].map((ch, i) => (
                <Box
                    key={ch}
                    aria-hidden
                    sx={{
                        position: "absolute",
                        zIndex: 1,
                        left: 40 + i * 90,
                        top: 150 + (i % 3) * 36,
                        width: 44,
                        height: 44,
                        borderRadius: 1,
                        opacity: 0.22,
                        bgcolor: "#0ea5e9",
                        color: "#fff",
                        fontWeight: 900,
                        fontSize: 22,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        filter: "blur(0.5px)",
                    }}
                >
                    {ch}
                </Box>
            ))}
            <AdHeadline
                title="EXPAND YOUR VOCABULARY"
                subtitle="Learn word definitions as you solve."
                sx={{ pt: isSquare ? 5 : 2 }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: isSquare ? 56 : 40,
                    top: isSquare ? 240 : 160,
                    zIndex: 3,
                    width: isSquare ? 320 : 280,
                }}
            >
                <FloatBadge
                    sx={{
                        bgcolor: "rgba(255,255,255,0.92)",
                        border: "1px solid rgba(14,165,233,0.35)",
                        color: "#0f172a",
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <SearchIcon sx={{ color: "#0284c7", fontSize: 36, mt: 0.5 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: 20, color: "#0c4a6e" }}>
                                MUSE
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.4 }}>
                                A person or force that is a source of inspiration.
                            </Typography>
                        </Box>
                    </Stack>
                </FloatBadge>
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: isSquare ? 40 : 18,
                    transform: "translateX(-50%)",
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    width={isSquare ? 360 : 280}
                    height={isSquare ? 720 : 520}
                    rotate={-2}
                />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: isSquare ? 64 : 48,
                    top: isSquare ? 320 : 200,
                    bgcolor: "rgba(16,185,129,0.95)",
                    border: "1px solid rgba(167,243,208,0.7)",
                }}
            >
                <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#ecfdf5" }}>
                    +50 XP
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#d1fae5", fontWeight: 700 }}>
                    Vocabulary Bonus
                </Typography>
            </FloatBadge>
        </AdCanvas>
    );
}

/** 06 — Three-device theme fan. */
function LandscapeThemes({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    const cascade = scene?.themeCascade || [
        { background: "iphone_4", theme: "random" },
        { background: "iphone_5", theme: "random" },
        { background: "iphone_6", theme: "random" },
    ];
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_5")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(90deg, rgba(12,74,110,0.45) 0%, rgba(20,83,45,0.35) 55%, rgba(5,46,22,0.55) 100%)"
            />
            <AdHeadline title="UNLOCK GORGEOUS THEMES" sx={{ pt: isSquare ? 5 : 2.5 }} />
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: isSquare ? 56 : 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    background={cascade[0]?.background}
                    theme={cascade[0]?.theme}
                    rotate={-14}
                    width={isSquare ? 250 : 200}
                    height={isSquare ? 520 : 420}
                    sx={{ mr: -4, zIndex: 1, opacity: 0.95 }}
                />
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    background={cascade[1]?.background || scene?.background}
                    theme={cascade[1]?.theme}
                    rotate={0}
                    width={isSquare ? 290 : 230}
                    height={isSquare ? 590 : 470}
                    sx={{ zIndex: 3, mb: 1 }}
                />
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    background={cascade[2]?.background}
                    theme={cascade[2]?.theme}
                    rotate={14}
                    width={isSquare ? 250 : 200}
                    height={isSquare ? 520 : 420}
                    sx={{ ml: -4, zIndex: 1, opacity: 0.95 }}
                />
            </Box>
        </AdCanvas>
    );
}

/** 07 — Daily + streak card. */
function LandscapeDaily({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_1")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="linear-gradient(180deg, rgba(2,6,23,0.45) 0%, rgba(15,23,42,0.28) 45%, rgba(2,6,23,0.55) 100%)"
            />
            {[...Array(24)].map((_, i) => (
                <Box
                    key={i}
                    aria-hidden
                    sx={{
                        position: "absolute",
                        zIndex: 1,
                        width: 2 + (i % 3),
                        height: 2 + (i % 3),
                        borderRadius: "50%",
                        bgcolor: "rgba(248,250,252,0.75)",
                        left: `${(i * 37) % 100}%`,
                        top: `${(i * 53) % 100}%`,
                        opacity: 0.35 + (i % 5) * 0.1,
                    }}
                />
            ))}
            <AdHeadline title="DAILY CHALLENGES & REWARDS" sx={{ pt: isSquare ? 5 : 2.5 }} />
            <Box
                sx={{
                    position: "absolute",
                    left: isSquare ? 88 : 72,
                    bottom: isSquare ? 56 : 28,
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    width={isSquare ? 360 : 280}
                    height={isSquare ? 720 : 520}
                    rotate={-4}
                />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: isSquare ? 80 : 64,
                    top: "50%",
                    transform: "translateY(-40%)",
                    width: isSquare ? 340 : 300,
                    textAlign: "center",
                    py: 2.5,
                }}
            >
                <EmojiEventsIcon sx={{ fontSize: 42, color: "#fbbf24" }} />
                <Typography
                    sx={{
                        mt: 1,
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: isSquare ? 30 : 26,
                        color: "#fde68a",
                    }}
                >
                    7 Day Streak!
                </Typography>
                <Stack
                    direction="row"
                    spacing={0.75}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mt: 1 }}
                >
                    <LocalFireDepartmentIcon sx={{ color: "#f97316" }} />
                    <Typography sx={{ fontWeight: 700, color: "#e2e8f0", fontSize: 14 }}>
                        Streak Reward unlocked
                    </Typography>
                </Stack>
            </FloatBadge>
        </AdCanvas>
    );
}

/** 08 — CTA showcase. */
function LandscapeCta({ puzzle, foundWords, scene }) {
    const isSquare = useIsSquare();
    const leftBadges = [
        { label: "Offline Play Available", Icon: WifiOffIcon },
        { label: "No Time Limits", Icon: TimerOutlinedIcon },
    ];
    const rightBadges = [
        { label: "Brain Training", Icon: LightbulbOutlinedIcon },
        { label: "Daily Rewards", Icon: ExtensionIcon },
    ];
    return (
        <AdCanvas>
            <CoverBg
                src={screenshotIphoneBgSrc(scene?.background, "iphone_6")}
                fallback={IPHONE_BG_PLAY_FALLBACK}
                overlay="radial-gradient(ellipse 55% 70% at 50% 60%, rgba(56,189,248,0.22) 0%, transparent 55%), linear-gradient(165deg, rgba(2,6,23,0.45) 0%, rgba(8,47,73,0.35) 50%, rgba(8,47,73,0.5) 100%)"
            />
            <AdHeadline title="READY FOR THE QUEST?" sx={{ pt: isSquare ? 5 : 2 }} />
            <Stack
                spacing={1.25}
                sx={{
                    position: "absolute",
                    left: isSquare ? 48 : 40,
                    top: isSquare ? 220 : 160,
                    zIndex: 3,
                    width: isSquare ? 240 : 220,
                }}
            >
                {leftBadges.map(({ label, Icon }) => (
                    <FloatBadge key={label} sx={{ py: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Icon sx={{ color: "#7dd3fc" }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#e0f2fe" }}>
                                {label}
                            </Typography>
                        </Stack>
                    </FloatBadge>
                ))}
            </Stack>
            <Stack
                spacing={1.25}
                sx={{
                    position: "absolute",
                    right: isSquare ? 48 : 40,
                    top: isSquare ? 220 : 160,
                    zIndex: 3,
                    width: isSquare ? 220 : 200,
                }}
            >
                {rightBadges.map(({ label, Icon }) => (
                    <FloatBadge key={label} sx={{ py: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Icon sx={{ color: "#7dd3fc" }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#e0f2fe" }}>
                                {label}
                            </Typography>
                        </Stack>
                    </FloatBadge>
                ))}
            </Stack>
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: isSquare ? 110 : 70,
                    transform: "translateX(-50%) perspective(600px) rotateX(8deg)",
                    zIndex: 3,
                }}
            >
                <PlayPhone
                    puzzle={puzzle}
                    foundWords={foundWords}
                    scene={scene}
                    width={isSquare ? 360 : 280}
                    height={isSquare ? 680 : 500}
                />
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: isSquare ? 36 : 18,
                    transform: "translateX(-50%)",
                    zIndex: 4,
                    px: 4,
                    py: 1.1,
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #0ea5e9, #38bdf8, #22d3ee)",
                    boxShadow: "0 8px 24px rgba(14,165,233,0.55)",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 20,
                        color: "#082f49",
                        letterSpacing: "0.06em",
                    }}
                >
                    PLAY NOW
                </Typography>
            </Box>
        </AdCanvas>
    );
}

/**
 * Google Ads creative for Word Wheel screenshot scenes 01–08.
 * `variant`: `"landscape"` (1200×628) or `"square"` (1200×1200).
 * App Store portrait pages are unchanged (no googleAd* creative params).
 */
export default function LandscapeGoogleAdCreative({
    scene,
    puzzle,
    foundWords = [],
    variant = "landscape",
}) {
    const layout = scene?.layout || "gameplay";
    const shared = { puzzle, foundWords, scene };

    let content = null;
    switch (layout) {
        case "hero":
            content = <LandscapeHero {...shared} />;
            break;
        case "progression":
            content = <LandscapeProgression {...shared} />;
            break;
        case "journey":
            content = <LandscapeJourney {...shared} />;
            break;
        case "dictionary":
            content = <LandscapeDictionary {...shared} />;
            break;
        case "themes":
            content = <LandscapeThemes {...shared} />;
            break;
        case "daily":
            content = <LandscapeDaily {...shared} />;
            break;
        case "cta":
            content = <LandscapeCta {...shared} />;
            break;
        case "masterJourney":
            // Master pack journey on the same photo as the journey map.
            content = <LandscapeMasterJourney scene={scene} />;
            break;
        case "clue":
            content = <LandscapeClue {...shared} />;
            break;
        case "gameplay":
        default:
            content = <LandscapeGameplay {...shared} />;
            break;
    }

    return (
        <AdVariantContext.Provider value={variant === "square" ? "square" : "landscape"}>
            {content}
        </AdVariantContext.Provider>
    );
}
